import { batch } from "react-redux";
import { Dispatch } from "redux";
import { v4 as uuidv4 } from "uuid";
import { emitEvent } from "@analytics/emit";
import { EventFields, EventNames } from "@analytics/enums";
import {
  GIFTING_RESULT_SUCCESS,
  mapErrorToGiftingResult,
} from "@analytics/gifting/giftingResult";
import { buyTicket } from "api/stream";
import { selectBaseGiftingParamV2 } from "src/analytics/gifting/selectBaseGiftingParams";
import { GiftPlaceholder } from "src/core/analytics/enums";
import { getIsSendGiftBIv2Enabled } from "src/features/gifts/giftsSoc";
import { RootState } from "src/state/delegate";
import {
  boughtTicket,
  failedToBuyTicket,
} from "state/actionCreators/viewerSession";

export interface PrivateStreamAnalyticEventsParams {
  [EventFields.GIFT_ID]: string;
  [EventFields.GIFT_SOURCE]: string;
  [EventFields.ONE_CLICK_GIFTING]: number;
  [EventFields.STREAM_ID]: string;
  [EventFields.STREAM_KIND]?: number;
  [EventFields.TICKET_PRICE]: number;
}

export const executeBuyTicket =
  (
    streamId: string,
    analyticEvents: PrivateStreamAnalyticEventsParams,
    isOneClickPurchase: boolean
  ) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const giftId = analyticEvents[EventFields.GIFT_ID];
    const isSendGiftBIv2Enabled = getIsSendGiftBIv2Enabled(state);
    const trackingId = uuidv4();
    const analyticParamsV2 = selectBaseGiftingParamV2(state, giftId);

    return buyTicket(streamId).then(
      (balance) => {
        batch(() => {
          if (isSendGiftBIv2Enabled) {
            emitEvent(EventNames.SEND_GIFT_V2, {
              ...analyticParamsV2,
              [EventFields.TRACKING_ID]: trackingId,
              [EventFields.IS_ONE_CLICK_GIFT]: false,
              [EventFields.RESULT]: 1,
              [EventFields.ONE_CLICK_PURCHASE]: Boolean(
                analyticEvents[EventFields.ONE_CLICK_GIFTING]
              ),
              [EventFields.GIFT_PLACEHOLDER]: GiftPlaceholder.PREMIUM_ENTRANCE,
            });
          }
          emitEvent(EventNames.SEND_GIFT, {
            ...analyticEvents,
            [EventFields.ONE_CLICK_PURCHASE]: Number(isOneClickPurchase),
            [EventFields.RESULT]: GIFTING_RESULT_SUCCESS,
          });
          dispatch(boughtTicket({ streamId, balance }));
        });
      },
      (error) => {
        if (isSendGiftBIv2Enabled) {
          emitEvent(EventNames.SEND_GIFT_V2, {
            ...analyticEvents,
            ...analyticParamsV2,
            [EventFields.TRACKING_ID]: trackingId,
            [EventFields.IS_ONE_CLICK_GIFT]: false,
            [EventFields.ONE_CLICK_PURCHASE]: Boolean(
              analyticEvents[EventFields.ONE_CLICK_GIFTING]
            ),
          });
        }
        emitEvent(EventNames.SEND_GIFT, {
          ...analyticEvents,
          [EventFields.ONE_CLICK_PURCHASE]: Number(isOneClickPurchase),
          [EventFields.RESULT]: mapErrorToGiftingResult(error),
          [EventFields.GIFT_PLACEHOLDER]: GiftPlaceholder.PREMIUM_ENTRANCE,
        });

        return dispatch(failedToBuyTicket(error));
      }
    );
  };
